import {FilterOption} from 'pages/DeliveryPlanner/components/Filter/Filter';
import {useEffect, useReducer, useState} from 'react';
import {SelectOption} from 'types';

type FilterKeysForSessionStorage =
  | 'user_location'
  | 'user_paid'
  | 'user_priority'
  | 'user_preferenceTime'
  | 'user_date'
  | 'user_attempt_start'
  | 'user_attempt_end'
  | 'user_filter_landlord'
  | 'user_property_address'
  | 'user_property_name'
  | 'user_createdDate'
  | 'user_offering_type';

// Common type for optional filter select options
type FilterSelectOption = SelectOption<string, string> | undefined;

// Filter state type
export type FilterState = {
  location: FilterOption[];
  date?: Date;
  preferenceTime?: FilterSelectOption;
  priority?: FilterSelectOption;
  paid?: FilterSelectOption;
  attemptStart?: FilterSelectOption;
  attemptEnd?: FilterSelectOption;
  propertyName?: FilterSelectOption;
  propertyAddress?: FilterSelectOption;
  landlord?: FilterSelectOption;
  createdDate: {
    start?: Date;
    end?: Date;
  };
  offeringType?: FilterSelectOption;
};
const blankFilterState: FilterState = {
  location: [],
  date: undefined,
  preferenceTime: undefined,
  priority: undefined,
  paid: undefined,
  createdDate: {start: undefined, end: undefined},
  attemptStart: undefined,
  attemptEnd: undefined,
  propertyName: undefined,
  propertyAddress: undefined,
  landlord: undefined,
  offeringType: undefined,
};
// Filter actions
type FilterAction =
  | {type: 'SET_MULTIPLE'; payload: Partial<FilterState>}
  | {type: 'RESET'};

// Filter reducer
const filterReducer = (
  state: FilterState,
  action: FilterAction
): FilterState => {
  switch (action.type) {
    case 'SET_MULTIPLE':
      return {...state, ...action.payload};
    case 'RESET':
      return blankFilterState;

    default:
      return state;
  }
};

// Helper functions for sessionStorage
const getSessionItem = <T,>(
  key: FilterKeysForSessionStorage,
  defaultValue?: T
): T | undefined => {
  const item = sessionStorage.getItem(key);
  if (!item || item === '') return defaultValue;
  try {
    return JSON.parse(item);
  } catch {
    return defaultValue;
  }
};

const setSessionItem = (key: FilterKeysForSessionStorage, value: any) => {
  sessionStorage.setItem(key, value ? JSON.stringify(value) : '');
};

export const useFilter = () => {
  // Initialize filter state from sessionStorage
  const initialCreatedDate = getSessionItem<{start: string; end: string}>(
    'user_createdDate'
  );
  const initialCreatedDateParsed =
    initialCreatedDate && (initialCreatedDate.start || initialCreatedDate.end)
      ? {
          start: new Date(initialCreatedDate.start),
          end: new Date(initialCreatedDate.end),
        }
      : {start: undefined, end: undefined};

  const initialFilterState: FilterState = {
    location: [],
    date: getSessionItem<string>('user_date')
      ? new Date(getSessionItem<string>('user_date')!)
      : undefined,
    preferenceTime: getSessionItem('user_preferenceTime'),
    priority: getSessionItem('user_priority'),
    paid: getSessionItem('user_paid'),
    attemptStart: getSessionItem('user_attempt_start'),
    attemptEnd: getSessionItem('user_attempt_end'),
    propertyName: getSessionItem('user_property_name'),
    propertyAddress: getSessionItem('user_property_address'),
    landlord: getSessionItem('user_filter_landlord'),
    createdDate: initialCreatedDateParsed,
    offeringType: getSessionItem('user_offering_type'),
  };

  const [filters, dispatchFilter] = useReducer(
    filterReducer,
    initialFilterState
  );
  const [tempFilters, setTempFilters] =
    useState<FilterState>(initialFilterState);

  //   Sync filter state to sessionStorage
  useEffect(() => {
    const filterItems: Record<FilterKeysForSessionStorage, any> = {
      user_location: filters.location,
      user_paid: filters.paid,
      user_priority: filters.priority,
      user_preferenceTime: filters.preferenceTime,
      user_date: filters.date,
      user_attempt_start: filters.attemptStart,
      user_attempt_end: filters.attemptEnd,
      user_filter_landlord: filters.landlord,
      user_property_address: filters.propertyAddress,
      user_property_name: filters.propertyName,
      user_createdDate: filters.createdDate,
      user_offering_type: filters.offeringType,
    };

    Object.entries(filterItems).forEach(([key, value]) => {
      setSessionItem(key as FilterKeysForSessionStorage, value);
    });
  }, [filters]);

  const resetFilter = () => {
    setTempFilters(blankFilterState);
    dispatchFilter({type: 'RESET'});
  };
  const applyFilter = () => {
    dispatchFilter({type: 'SET_MULTIPLE', payload: tempFilters});
  };
  return {
    filters,
    dispatchFilter,
    tempFilters,
    setTempFilters,
    resetFilter,
    applyFilter,
  };
};
